import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Copy, Check, KeyRound, RefreshCw, ShieldAlert } from 'lucide-react'
import { WizardStepper } from '@/components/shared/WizardStepper'

const STEPS = ['Account Details', 'Access & Scopes']

const SCOPES = [
  { value: 'positions:read', label: 'Read positions', hint: 'Look a position up by their own identifier' },
  { value: 'positions:write', label: 'Create & manage positions', hint: 'Create, activate, and close positions' },
  { value: 'applications:write', label: 'Create applications', hint: 'Submit candidates and their CVs' },
  { value: 'interviews:write', label: 'Send interviews', hint: 'Create an interview and email the invitation' },
] as const

const schema = z.object({
  name: z.string().min(2),
  external_source: z.string().min(2).max(30),
  company_name: z.string().min(2),
  email: z.string().email(),
  username: z.string().min(3),
  phone: z.string().min(1),
  contact_email: z.string().email(),
  allowed_ips: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

const STEP_FIELDS: Array<(keyof FormValues)[]> = [
  ['name', 'external_source', 'company_name', 'email', 'username', 'phone', 'contact_email'],
  [],
]

interface Credentials {
  clientId: string
  clientSecret: string
  companyId?: string
  scopes?: string[]
  previousSecretExpiresAt?: string | null
}

function errorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  )
}

function CopyableSecret({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-border/40 font-mono text-sm break-all">
        <span className="flex-1 min-w-0 break-all text-slate-700 dark:text-slate-200">{value}</span>
        <Button variant="ghost" size="icon" onClick={handleCopy} className="shrink-0">
          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
        </Button>
      </div>
    </div>
  )
}

function CredentialsPanel({
  credentials,
  heading,
  onReset,
  resetLabel,
}: {
  credentials: Credentials
  heading: string
  onReset: () => void
  resetLabel: string
}) {
  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">{heading}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          The secret is shown once and only its hash is stored. If it is lost, rotate it — it cannot be recovered.
        </p>
      </div>
      <div className="bento-card p-8 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-[#076698]/10 dark:bg-sky-900/40 flex items-center justify-center text-[#076698]">
            <KeyRound size={20} />
          </div>
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Partner credentials</p>
        </div>

        <CopyableSecret label="Client ID" value={credentials.clientId} />
        <CopyableSecret label="Client Secret" value={credentials.clientSecret} />

        {credentials.scopes?.length ? (
          <div className="space-y-1.5">
            <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Granted scopes
            </label>
            <div className="flex flex-wrap gap-2">
              {credentials.scopes.map((scope) => (
                <span
                  key={scope}
                  className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-mono text-slate-600 dark:text-slate-300"
                >
                  {scope}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {credentials.previousSecretExpiresAt ? (
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-400">
            <ShieldAlert size={16} className="shrink-0 mt-0.5" />
            <span>
              The previous secret keeps working until{' '}
              <strong>{new Date(credentials.previousSecretExpiresAt).toLocaleString()}</strong>, so the partner can
              deploy the new one on their own schedule.
            </span>
          </div>
        ) : null}

        <Button onClick={onReset} variant="outline">
          {resetLabel}
        </Button>
      </div>
    </div>
  )
}

export function PartnerClientsPage() {
  const [mode, setMode] = useState<'create' | 'rotate'>('create')
  const [step, setStep] = useState(0)
  const [scopes, setScopes] = useState<string[]>(SCOPES.map((s) => s.value))
  const [credentials, setCredentials] = useState<Credentials | null>(null)
  const [rotated, setRotated] = useState(false)
  const [serverError, setServerError] = useState('')
  const [rotateId, setRotateId] = useState('')
  const [rotating, setRotating] = useState(false)

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) as any })

  const handleNext = async () => {
    const valid = await trigger(STEP_FIELDS[step])
    if (valid) setStep((s) => s + 1)
  }

  const toggleScope = (value: string) => {
    setScopes((current) =>
      current.includes(value) ? current.filter((s) => s !== value) : [...current, value]
    )
  }

  const parseAllowedIps = (raw?: string) =>
    (raw ?? '')
      .split(/[,\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean)

  const onSubmit = async (values: FormValues) => {
    setServerError('')
    if (!scopes.length) {
      setServerError('Select at least one scope — a client with none can authenticate but do nothing.')
      return
    }
    try {
      const { data } = await flaskApi.post('/partner-clients', {
        ...values,
        scopes,
        allowed_ips: parseAllowedIps(values.allowed_ips),
      })
      setRotated(false)
      setCredentials(data)
    } catch (err: unknown) {
      setServerError(errorMessage(err, 'Failed to create partner client'))
    }
  }

  const onRotate = async () => {
    setServerError('')
    if (!rotateId.trim()) {
      setServerError('Enter the partner client id to rotate.')
      return
    }
    setRotating(true)
    try {
      const { data } = await flaskApi.post(`/partner-clients/${rotateId.trim()}/rotate-secret`, {})
      setRotated(true)
      setCredentials(data)
    } catch (err: unknown) {
      setServerError(errorMessage(err, 'Failed to rotate secret'))
    } finally {
      setRotating(false)
    }
  }

  const resetAll = () => {
    setCredentials(null)
    setRotated(false)
    setStep(0)
    setRotateId('')
    setServerError('')
  }

  if (credentials) {
    return (
      <CredentialsPanel
        credentials={credentials}
        heading={rotated ? 'Secret Rotated' : 'Partner Client Created'}
        onReset={resetAll}
        resetLabel={rotated ? 'Done' : 'Create Another'}
      />
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Partner API Clients</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Onboard a third-party integration, or rotate an existing client's secret.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          variant={mode === 'create' ? 'default' : 'outline'}
          className={mode === 'create' ? 'bg-[#076698] hover:bg-[#055580]' : ''}
          onClick={() => { setMode('create'); setServerError('') }}
        >
          Create Client
        </Button>
        <Button
          variant={mode === 'rotate' ? 'default' : 'outline'}
          className={mode === 'rotate' ? 'bg-[#076698] hover:bg-[#055580]' : ''}
          onClick={() => { setMode('rotate'); setServerError('') }}
        >
          Rotate Secret
        </Button>
      </div>

      {serverError && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
          <span className="shrink-0 mt-0.5">⚠</span>
          <span>{serverError}</span>
        </div>
      )}

      {mode === 'rotate' ? (
        <div className="bento-card p-7 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#076698]/10 dark:bg-sky-900/40 flex items-center justify-center text-[#076698]">
              <RefreshCw size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Rotate a client secret</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                The current secret keeps working for a grace period, so the partner can cut over without downtime.
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Partner Client ID
            </label>
            <Input
              value={rotateId}
              onChange={(event) => setRotateId(event.target.value)}
              placeholder="The client's record id, from the Partner Clients table"
            />
          </div>
          <Button
            onClick={onRotate}
            disabled={rotating}
            className="bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
          >
            {rotating ? 'Rotating...' : 'Rotate Secret'}
          </Button>
        </div>
      ) : (
        <div className="bento-card p-7 space-y-7">
          <div className="pb-6 border-b border-border/40">
            <WizardStepper steps={STEPS} current={step} />
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: 'name' as const, label: 'Integration Name' },
                  { name: 'external_source' as const, label: 'Source Key (e.g. sap)' },
                  { name: 'company_name' as const, label: 'Company Name' },
                  { name: 'email' as const, label: 'Owner Email', type: 'email' },
                  { name: 'username' as const, label: 'Owner Username' },
                  { name: 'phone' as const, label: 'Phone' },
                  { name: 'contact_email' as const, label: 'Contact Email', type: 'email' },
                ].map(({ name, label, type }) => (
                  <div key={name} className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {label}
                    </label>
                    <Input {...register(name)} type={type ?? 'text'} />
                    {errors[name] && <p className="text-xs text-destructive">{errors[name]?.message}</p>}
                  </div>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Grant only what this integration needs. Scopes are checked on every request, so narrowing them
                    later takes effect without reissuing credentials.
                  </p>
                  <div className="space-y-3 p-5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-border/40">
                    {SCOPES.map(({ value, label, hint }) => (
                      <label key={value} className="flex items-start gap-3 text-sm cursor-pointer font-medium">
                        <input
                          type="checkbox"
                          checked={scopes.includes(value)}
                          onChange={() => toggleScope(value)}
                          className="rounded mt-1"
                        />
                        <span>
                          {label}
                          <span className="block text-xs font-normal text-slate-500 dark:text-slate-400">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Allowed IPs (optional)
                  </label>
                  <Input
                    {...register('allowed_ips')}
                    placeholder="203.0.113.7, 198.51.100.0/24"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Comma separated; plain addresses or CIDR ranges. Leave empty to allow any origin.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between mt-7 pt-5 border-t border-border/40">
              <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)} disabled={step === 0}>
                ← Back
              </Button>
              <span className="text-[10px] font-bold text-slate-400 text-center uppercase tracking-widest">
                Step {step + 1} of {STEPS.length}
              </span>
              {step < STEPS.length - 1 ? (
                <Button
                  type="button"
                  className="bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
                  onClick={handleNext}
                >
                  Continue →
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Partner Client'}
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
